/**
 * Login Page Component
 *
 * Purpose: User authentication page with centered card design featuring Orchestrate
 *          brand identity on full-screen gradient background.
 *
 * Architecture: Page-level component following Figma design with centered card
 *              containing left brand panel and right login form panel.
 *
 * Dependencies: React, Material-UI, AuthContext, React Router, Google OAuth
 * Author: Development Team
 * Created: 2025-01-27
 * Last Updated: 2025-11-29 - Redesigned per Figma brand identity
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Link from '@mui/material/Link';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import LockOutlined from '@mui/icons-material/LockOutlined';
import { styled } from '@mui/material/styles';
import { useAuth } from '../../context/AuthContext';
import { GoogleLogin } from '@react-oauth/google';
import TenantSelection from '../../components/tenant-selection/TenantSelection';
import { brandColors } from '../../theme/orchestrateTheme';

/**
 * Full-screen gradient background wrapper
 */
const GradientBackground = styled(Box)({
  minHeight: '100vh',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  background: `linear-gradient(180deg, ${brandColors.aqua} 0%, ${brandColors.teal} 35%, ${brandColors.deepViolet} 100%)`,
});

/**
 * Main login card - contains both brand panel and form panel
 */
const LoginCard = styled(Card)({
  display: 'flex',
  maxWidth: 900,
  width: '100%',
  borderRadius: 16,
  overflow: 'hidden',
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  '@media (max-width: 768px)': {
    flexDirection: 'column',
    maxWidth: 450,
  },
});

/**
 * Left brand panel (purple)
 */
const BrandPanel = styled(Box)({
  flex: 1,
  backgroundColor: brandColors.deepViolet,
  padding: '48px 40px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  '@media (max-width: 768px)': {
    padding: '32px 24px',
  },
});

/**
 * Right form panel (white)
 */
const FormPanel = styled(Box)(({ theme: _theme }) => ({
  flex: 1,
  backgroundColor: '#ffffff',
  padding: '48px 40px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  '@media (max-width: 768px)': {
    padding: '32px 24px',
  },
}));

/**
 * Styled section for form elements
 */
const FormSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

/**
 * Form data interface for login form state
 */
interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

/**
 * Login page component
 */
/** Where the visitor was headed before sign-in, query string and hash included. */
const returnTo = (locationState: unknown): string => {
  const from = (locationState as { from?: { pathname?: string; search?: string; hash?: string } })
    ?.from;
  return from?.pathname ? `${from.pathname}${from.search || ''}${from.hash || ''}` : '/home';
};

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, login, googleLogin, clearError, selectTenant } = useAuth();

  // Form state management
  const [formData, setFormData] = useState<LoginFormData>({
    email: (location.state as { email?: string } | null)?.email ?? '',
    password: '',
    rememberMe: localStorage.getItem('loginRememberMe') === 'true',
  });
  // Arrived from the reset page after choosing a new password (ORCH-296)
  const passwordReset = Boolean(
    (location.state as { passwordReset?: boolean } | null)?.passwordReset
  );

  // UI state management
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  // Security state management
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isLockedOut, setIsLockedOut] = useState(false);
  const [lockoutTime, setLockoutTime] = useState<Date | null>(null);
  const [remainingAttempts, setRemainingAttempts] = useState(3);

  // Tenant selection state
  const [showTenantSelection, setShowTenantSelection] = useState(false);

  /**
   * Memoize button disabled state to ensure it updates when state changes
   * Note: Lockout no longer disables the button - it's checked on submit instead
   */
  const isButtonDisabled = useMemo(() => {
    return state.loading;
  }, [state.loading]);

  /**
   * Handles post-login flow
   */
  useEffect(() => {
    if (state.isAuthenticated && state.user) {
      if (state.user.activeTenant) {
        const from = returnTo(location.state);
        navigate(from, { replace: true });
      } else {
        if (state.user.availableTenants && state.user.availableTenants.length > 0) {
          if (state.user.availableTenants.length === 1) {
            const singleTenant = state.user.availableTenants[0];
            selectTenant(singleTenant.tenant_id)
              .then(() => {
                const from = returnTo(location.state);
                navigate(from, { replace: true });
              })
              .catch((error) => {
                console.error('Failed to auto-select tenant:', error);
                setShowTenantSelection(true);
              });
          } else {
            setShowTenantSelection(true);
          }
        } else {
          const from = returnTo(location.state);
          navigate(from, { replace: true });
        }
      }
    }
  }, [state.isAuthenticated, state.user, navigate, location.state, selectTenant]);

  /**
   * Checks for existing account lockout on component mount
   */
  useEffect(() => {
    const storedLockout = localStorage.getItem('loginLockout');
    if (storedLockout) {
      const lockoutData = JSON.parse(storedLockout);
      const lockoutExpiry = new Date(lockoutData.expiry);

      if (lockoutExpiry > new Date()) {
        setIsLockedOut(true);
        setLockoutTime(lockoutExpiry);
        setLoginAttempts(lockoutData.attempts);
        setRemainingAttempts(0);
      } else {
        localStorage.removeItem('loginLockout');
        setLoginAttempts(0);
        setRemainingAttempts(3);
      }
    }
  }, []);

  /**
   * Validates form input fields
   */
  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Handles form input changes
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (name === 'rememberMe' && type === 'checkbox') {
      localStorage.setItem('loginRememberMe', checked ? 'true' : 'false');
    }

    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }

    // Clear error message when user starts typing, but don't interfere with loading state
    // The loading state should already be false after LOGIN_FAILURE, but this ensures
    // the error message is cleared for better UX
    if (state.error) {
      clearError();
    }
  };

  /**
   * Handles form submission
   */
  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    // Check if lockout is still active by checking localStorage
    const storedLockout = localStorage.getItem('loginLockout');
    if (storedLockout) {
      const lockoutData = JSON.parse(storedLockout);
      const lockoutExpiry = new Date(lockoutData.expiry);

      if (lockoutExpiry > new Date()) {
        // Lockout still active - update state to show lockout message
        setIsLockedOut(true);
        setLockoutTime(lockoutExpiry);
        setLoginAttempts(lockoutData.attempts);
        setRemainingAttempts(0);
        return;
      } else {
        // Lockout expired - clear it and allow login
        localStorage.removeItem('loginLockout');
        setIsLockedOut(false);
        setLockoutTime(null);
        setLoginAttempts(0);
        setRemainingAttempts(3);
      }
    }

    if (!validateForm()) {
      return;
    }

    try {
      await login({
        email: formData.email,
        password: formData.password,
      });

      setLoginAttempts(0);
      setRemainingAttempts(3);
      localStorage.removeItem('loginLockout');
    } catch (error: any) {
      const newAttempts = loginAttempts + 1;
      setLoginAttempts(newAttempts);
      setRemainingAttempts(3 - newAttempts);

      if (newAttempts >= 3) {
        setIsLockedOut(true);
        const lockoutExpiry = new Date(Date.now() + 15 * 60 * 1000);
        setLockoutTime(lockoutExpiry);

        localStorage.setItem(
          'loginLockout',
          JSON.stringify({
            expiry: lockoutExpiry.toISOString(),
            attempts: newAttempts,
          })
        );
      }
    }
  };

  const handleClickShowPassword = (): void => {
    setShowPassword(!showPassword);
  };

  const getRemainingLockoutTime = (): number => {
    if (!lockoutTime) return 0;
    const now = new Date();
    const remaining = Math.max(0, lockoutTime.getTime() - now.getTime());
    return Math.ceil(remaining / 1000 / 60);
  };

  const handleGoogleSuccess = async (credentialResponse: any): Promise<void> => {
    try {
      await googleLogin(credentialResponse.credential);
    } catch (error) {
      console.error('Google OAuth failed:', error);
    }
  };

  const handleGoogleError = (): void => {
    console.error('Google OAuth error');
    alert('Google login failed. Please try again.');
  };

  const handleSSOLogin = (): void => {
    alert('SSO is not yet implemented. Please use email/password login.');
  };

  return (
    <GradientBackground>
      <LoginCard>
        {/* Left Brand Panel */}
        <BrandPanel>
          {/* Logo and Brand Name */}
          <Box
            data-testid="login-brand-lockup"
            sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 6 }}
          >
            <Box
              component="img"
              data-testid="login-brand-logo"
              src="/images/orchestrate-logo.png"
              alt="Orchestrate"
              sx={{
                width: 80,
                height: 80,
                objectFit: 'contain',
              }}
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                e.currentTarget.style.display = 'none';
              }}
            />
            <Typography
              data-testid="login-brand-wordmark"
              sx={{
                color: 'white',
                fontWeight: 600,
                fontSize: '2rem',
                letterSpacing: '-0.01em',
              }}
            >
              Orchestrate
            </Typography>
          </Box>

          {/* Tagline */}
          <Typography
            variant="h4"
            sx={{
              color: 'white',
              fontWeight: 700,
              lineHeight: 1.3,
              mb: 4,
              fontSize: { xs: '1.75rem', md: '2rem' },
            }}
          >
            Welcome to the future of media planning.
          </Typography>

          {/* Features List */}
          <Box sx={{ color: 'rgba(255,255,255,0.85)' }}>
            <Typography
              variant="body2"
              sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              • Automated workflows
            </Typography>
            <Typography
              variant="body2"
              sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              • Real time campaign monitoring
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              • Build a robust media plan in minutes
            </Typography>
          </Box>
        </BrandPanel>

        {/* Right Form Panel */}
        <FormPanel>
          {/* Header */}
          <Typography
            variant="h5"
            sx={{
              mb: 3,
              fontWeight: 600,
              color: 'text.secondary',
            }}
          >
            Sign In
          </Typography>

          {passwordReset && !state.error && (
            <Alert severity="success" sx={{ mb: 2 }} data-testid="password-reset-done">
              Password changed. Sign in with your new password.
            </Alert>
          )}

          {/* Error Alert */}
          {state.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {state.error}
            </Alert>
          )}

          {/* Lockout Alert */}
          {isLockedOut && (
            <Alert severity="warning" sx={{ mb: 2 }} icon={<LockOutlined />}>
              <Typography variant="body2" gutterBottom>
                Account temporarily locked due to multiple failed login attempts.
              </Typography>
              <Typography variant="body2">
                Please try again in {getRemainingLockoutTime()} minutes.
              </Typography>
            </Alert>
          )}

          {/* Remaining Attempts Alert */}
          {!isLockedOut && loginAttempts > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {remainingAttempts} login attempt{remainingAttempts !== 1 ? 's' : ''} remaining before
              account lockout.
            </Alert>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} noValidate>
            <FormSection>
              {/* Email Field */}
              <TextField
                fullWidth
                name="email"
                type="email"
                label="Email Address"
                value={formData.email}
                onChange={handleInputChange}
                error={!!formErrors.email}
                helperText={formErrors.email}
                disabled={state.loading}
                autoComplete="email"
                required
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />

              {/* Password Field */}
              <TextField
                fullWidth
                name="password"
                type={showPassword ? 'text' : 'password'}
                label="Password"
                value={formData.password}
                onChange={handleInputChange}
                error={!!formErrors.password}
                helperText={formErrors.password}
                disabled={state.loading}
                autoComplete="current-password"
                required
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleClickShowPassword}
                        edge="end"
                        disabled={state.loading}
                        aria-label={showPassword ? 'hide password' : 'show password'}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Remember Me and Forgot Password */}
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <FormControlLabel
                  control={
                    <Checkbox
                      name="rememberMe"
                      checked={formData.rememberMe}
                      onChange={handleInputChange}
                      disabled={state.loading}
                      color="primary"
                    />
                  }
                  label={<Typography variant="body2">Remember me</Typography>}
                />
                <Link href="#" variant="body2" sx={{ color: brandColors.purple }}>
                  Forgot Password?
                </Link>
              </Box>

              {/* Login Button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isButtonDisabled}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  bgcolor: brandColors.teal,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: brandColors.deepTeal,
                  },
                }}
              >
                {state.loading ? <CircularProgress size={24} color="inherit" /> : 'Continue'}
              </Button>

              {/* Divider */}
              <Divider sx={{ my: 1 }}>
                <Typography variant="body2" color="textSecondary">
                  OR
                </Typography>
              </Divider>

              {/* Social Login Buttons */}
              <Box>
                {!state.loading ? (
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    theme="outline"
                    size="large"
                    width="100%"
                    text="continue_with"
                  />
                ) : (
                  <Button fullWidth variant="outlined" disabled sx={{ borderRadius: 2, py: 1.25 }}>
                    Continue with Google
                  </Button>
                )}
              </Box>

              <Button
                fullWidth
                variant="outlined"
                disabled={state.loading}
                onClick={handleSSOLogin}
                sx={{
                  borderRadius: 2,
                  py: 1.25,
                  textTransform: 'none',
                  color: brandColors.teal,
                  borderColor: brandColors.teal,
                  '&:hover': {
                    borderColor: brandColors.deepTeal,
                    backgroundColor: 'rgba(18, 106, 99, 0.04)',
                  },
                }}
              >
                Continue with SSO
              </Button>

              {/* Sign Up Link */}
              <Box textAlign="center" mt={1}>
                <Typography variant="body2" color="textSecondary">
                  Don't have an account?{' '}
                  <Link href="#" sx={{ color: brandColors.purple }}>
                    Contact your administrator
                  </Link>
                </Typography>
              </Box>
            </FormSection>
          </form>
        </FormPanel>
      </LoginCard>

      {/* Tenant Selection Modal */}
      <TenantSelection open={showTenantSelection} onClose={() => setShowTenantSelection(false)} />
    </GradientBackground>
  );
};

export default LoginPage;
